import { divides, formatDuration, humanDuration } from './duration';
import { REFERENCES, type Reference } from './links';
import type { ResolvedPlan } from './resolve';
import type { StorageEstimate } from './storage';
import type { Aggregate, DesignConfig } from './types';

export type Severity = 'error' | 'warning' | 'info';

export interface Warning {
  /** Stable rule id, also used as the React key and in tests. */
  id: string;
  severity: Severity;
  /** One-line summary. */
  title: string;
  /** The concrete detail, with the user's own numbers substituted in. */
  detail: string;
  /** Tier this applies to, or `null` for design-wide findings. */
  tierIndex: number | null;
  /** Background article explaining this class of problem. */
  reference: Reference;
}

/** Series count above which a rollup bucket starts to hurt query planning. */
export const CARDINALITY_LIMIT = 100_000;

/**
 * Aggregates that do *not* survive a second pass over already-aggregated data.
 * min, max, sum and last are associative — re-applying them to partial results gives
 * the same answer as applying them to the raw data. mean and median are not.
 */
const NON_CASCADING: ReadonlySet<Aggregate> = new Set<Aggregate>(['mean', 'median']);

/**
 * Run every rule against a resolved plan.
 *
 * Rules only fire on things that are actually wrong or actually surprising —
 * a panel full of noise gets ignored, and then the one real finding gets ignored too.
 * Results are sorted error → warning → info, then by tier.
 */
export function analyse(
  config: DesignConfig,
  plan: ResolvedPlan,
  estimate: StorageEstimate,
): Warning[] {
  const out: Warning[] = [];
  const push = (w: Warning) => out.push(w);

  for (const err of plan.parseErrors) {
    push({
      id: `parse-error:${err.path}`,
      severity: 'error',
      title: `${err.label} is not a valid duration`,
      detail:
        `${JSON.stringify(err.value)} is not a Flux duration. Use a magnitude and a fixed-length ` +
        `unit such as 30s, 5m, 1h30m or 7d. Calendar units (mo, y) are rejected because their ` +
        `length varies, which breaks window alignment. Estimates below use a placeholder value.`,
      tierIndex: null,
      reference: REFERENCES.windowMapping,
    });
  }

  if (plan.tiers.length === 0) {
    push({
      id: 'no-tiers',
      severity: 'error',
      title: 'No downsampling tiers defined',
      detail:
        'Add at least one tier. Without a rollup destination the raw bucket carries the whole ' +
        'retention window on its own, at full resolution.',
      tierIndex: null,
      reference: REFERENCES.retention,
    });
  }

  plan.tiers.forEach((tier, i) => {
    const tierLabel = `Tier ${i + 1} (${tier.bucket})`;

    // --- Scheduling -------------------------------------------------------
    if (tier.offsetSeconds >= tier.everySeconds && tier.everySeconds > 0) {
      push({
        id: `offset-ge-every:${i}`,
        severity: 'error',
        title: `${tierLabel}: offset is not shorter than every`,
        detail:
          `An offset of ${formatDuration(tier.offsetSeconds)} against an every of ` +
          `${formatDuration(tier.everySeconds)} pushes each run into the following schedule slot. ` +
          `Runs queue behind each other and the effective cadence drifts. Keep the offset well ` +
          `under one interval — it exists to wait out late writes, not to reschedule the task.`,
        tierIndex: i,
        reference: REFERENCES.offsetTuning,
      });
    }

    if (tier.everySeconds < tier.windowSeconds) {
      push({
        id: `every-shorter-than-window:${i}`,
        severity: 'warning',
        title: `${tierLabel}: task runs more often than a window closes`,
        detail:
          `The task runs every ${formatDuration(tier.everySeconds)} but aggregates over ` +
          `${formatDuration(tier.windowSeconds)}. Most runs re-derive and rewrite a window that ` +
          `has not changed, burning query capacity for no new data. Setting every equal to the ` +
          `window is the usual choice.`,
        tierIndex: i,
        reference: REFERENCES.offsetTuning,
      });
    }

    // --- Window mapping ---------------------------------------------------
    if (tier.windowSeconds < tier.sourceResolutionSeconds) {
      push({
        id: `window-finer-than-source:${i}`,
        severity: 'error',
        title: `${tierLabel}: window is finer than its input`,
        detail:
          `Aggregating ${humanDuration(tier.sourceResolutionSeconds)} data into ` +
          `${humanDuration(tier.windowSeconds)} windows does not add resolution — it fabricates ` +
          `empty or single-sample windows and stores more points than the input contains. ` +
          `A tier window must be coarser than the data feeding it.`,
        tierIndex: i,
        reference: REFERENCES.windowMapping,
      });
    } else if (!divides(tier.sourceResolutionSeconds, tier.windowSeconds)) {
      push({
        id: `window-not-multiple:${i}`,
        severity: 'error',
        title: `${tierLabel}: window is not a whole multiple of its input`,
        detail:
          `A ${formatDuration(tier.windowSeconds)} window over ` +
          `${formatDuration(tier.sourceResolutionSeconds)} data leaves ` +
          `${(tier.windowSeconds / tier.sourceResolutionSeconds).toFixed(2)} samples per window. ` +
          `Window boundaries then cut across samples, so consecutive windows contain different ` +
          `sample counts and every aggregate is subtly weighted by that count.`,
        tierIndex: i,
        reference: REFERENCES.windowMapping,
      });
    }

    // --- Cascade correctness ---------------------------------------------
    if (tier.retentionSeconds < tier.sourceRetentionSeconds) {
      push({
        id: `retention-gap:${i}`,
        severity: 'error',
        title: `${tierLabel}: retention is shorter than its input's`,
        detail:
          `This tier keeps ${humanDuration(tier.retentionSeconds)} but reads from "` +
          `${tier.sourceBucket}", which keeps ${humanDuration(tier.sourceRetentionSeconds)}. ` +
          `The rollup expires before the data it summarises, so the long-range history it exists ` +
          `to provide disappears first. Retention must grow as resolution coarsens.`,
        tierIndex: i,
        reference: REFERENCES.cascading,
      });
    }

    if (!tier.fromRaw) {
      const parent = tier.sourceAggregates;
      const counting = parent.includes('count') || parent.includes('sum');
      if (tier.aggregates.includes('count') && counting) {
        push({
          id: `count-of-count:${i}`,
          severity: 'error',
          title: `${tierLabel}: count over already-counted data`,
          detail:
            `"${tier.sourceBucket}" already reduced the data with ${parent.join('/')}. Applying ` +
            `count here counts rollup rows, not original events, so the number silently becomes ` +
            `the number of parent windows. Use sum to carry a count down the cascade.`,
          tierIndex: i,
          reference: REFERENCES.cascading,
        });
      }
      if (tier.aggregates.includes('sum') && parent.includes('mean')) {
        push({
          id: `sum-of-mean:${i}`,
          severity: 'error',
          title: `${tierLabel}: sum over averaged data`,
          detail:
            `"${tier.sourceBucket}" stores means. Summing means produces a value with no physical ` +
            `meaning — it scales with the number of parent windows rather than with the data. ` +
            `Sum must be computed from the raw bucket, or carried down as sum at every hop.`,
          tierIndex: i,
          reference: REFERENCES.cascading,
        });
      }
      const nonCascading = tier.aggregates.filter((a) => NON_CASCADING.has(a));
      if (nonCascading.length > 0 && parent.some((a) => NON_CASCADING.has(a))) {
        push({
          id: `mean-of-mean:${i}`,
          severity: 'warning',
          title: `${tierLabel}: ${nonCascading.join('/')} over already-averaged data`,
          detail:
            `Averaging averages is only unbiased when every parent window held the same number ` +
            `of samples. Gaps, restarts and uneven ingest break that, and the error compounds at ` +
            `each hop. min, max, sum and last cascade cleanly; mean and median do not. Rolling ` +
            `this tier straight from the raw bucket avoids the bias entirely.`,
          tierIndex: i,
          reference: REFERENCES.cascading,
        });
      }
    }

    // --- Aggregate choice -------------------------------------------------
    if (tier.fromRaw && tier.aggregates.length === 1 && tier.aggregates[0] === 'mean') {
      push({
        id: `mean-only:${i}`,
        severity: 'info',
        title: `${tierLabel}: mean is the only aggregate kept`,
        detail:
          'A lone mean is the one summary that cannot show you an excursion: a sensor spiking ' +
          'for ten seconds inside a one-minute window vanishes completely, and once the raw ' +
          'bucket expires it is unrecoverable. Keeping min and max alongside costs two extra ' +
          'series per input series and preserves the envelope; median is the better centre ' +
          'statistic when the signal is noisy.',
        tierIndex: i,
        reference: REFERENCES.meanVsMedian,
      });
    }

    // --- Cardinality ------------------------------------------------------
    const tierEstimate = estimate.tiers[i];
    if (tierEstimate && tierEstimate.seriesCount > CARDINALITY_LIMIT) {
      const retained = config.source.tagKeys.filter((t) => t.retained && t.key.trim() !== '');
      const worst = [...retained].sort((a, b) => b.cardinality - a.cardinality)[0];
      push({
        id: `cardinality:${i}`,
        severity: 'warning',
        title: `${tierLabel}: ~${Math.round(tierEstimate.seriesCount).toLocaleString('en-US')} series`,
        detail:
          `Series count is the product of every retained tag, the field count and each aggregate ` +
          `in the cascade, and it is what the index has to hold in memory — not the point count. ` +
          (worst
            ? `"${worst.key}" alone contributes a factor of ${worst.cardinality}. Dropping tags a ` +
              `rollup does not need at the first hop is the cheapest fix; `
            : '') +
          `so is trimming the aggregate list, since each aggregate multiplies every downstream tier.`,
        tierIndex: i,
        reference: REFERENCES.cardinality,
      });
    }

    if (tier.createEmpty) {
      push({
        id: `create-empty:${i}`,
        severity: 'info',
        title: `${tierLabel}: createEmpty is true`,
        detail:
          'Empty windows are materialised as null points. That makes gaps explicit for charting, ' +
          'but it also means a series that stops reporting keeps consuming a point per window ' +
          'for the whole retention period, and the storage estimate below assumes every series ' +
          'reports continuously.',
        tierIndex: i,
        reference: REFERENCES.cardinality,
      });
    }
  });

  // --- Design-wide ------------------------------------------------------
  if (plan.tiers.length > 0 && plan.tiers.every((t) => t.offsetSeconds === 0)) {
    push({
      id: 'no-offset',
      severity: 'info',
      title: 'No task offset configured',
      detail:
        'With a zero offset every task fires the instant its window closes, so a write still in ' +
        'flight is missed on the first pass. An offset sized to your ingest lag costs nothing and ' +
        'removes a whole class of silently-missing rollup points. Different measurements often ' +
        'need different offsets — the slowest publisher sets the floor.',
      tierIndex: null,
      reference: REFERENCES.offsetTuning,
    });
  }

  if (estimate.savedBytes < 0) {
    push({
      id: 'negative-saving',
      severity: 'warning',
      title: 'The tiered design stores more than raw-only would',
      detail:
        'Adding up every tier at its own retention exceeds the cost of simply keeping raw for ' +
        'the whole horizon. That happens when tiers keep too many aggregates, or when the ' +
        'windows are too close together to shed enough points to pay for the extra buckets.',
      tierIndex: null,
      reference: REFERENCES.retention,
    });
  }

  const rank: Record<Severity, number> = { error: 0, warning: 1, info: 2 };
  return out.sort(
    (a, b) => rank[a.severity] - rank[b.severity] || (a.tierIndex ?? -1) - (b.tierIndex ?? -1),
  );
}
